import type { CmsTranslations } from "./locales";

/**
 * 繁體中文 (台灣) — FireCMS chrome only.
 *
 * Covers the strings an editor meets in the actual authoring flow. FireCMS
 * falls back to English per key (`fallbackLng: "en"` inside
 * `FireCMSi18nProvider`), so the ~530 untranslated keys — the PRO collection
 * editor, roles and users admin, CSV import, billing — stay English and cost
 * nothing. Add a key here the moment an editor reports seeing English.
 *
 * **Field labels are NOT here.** Collection and property `name` /
 * `description` are plain strings in `src/collections/*`, not FireCMS chrome,
 * so this file cannot reach them. They follow the bilingual convention already
 * used for collection names (`最新資訊 News`).
 *
 * Terminology is Taiwan usage, not mainland: 儲存 not 保存, 建立 not 创建,
 * 搜尋 not 搜索, 篩選 not 筛选.
 */
export const zhTW: CmsTranslations = {
    // Entity form actions
    save: "儲存",
    create: "建立",
    create_copy: "建立副本",
    save_and_close: "儲存並關閉",
    create_copy_and_close: "建立副本並關閉",
    create_and_close: "建立並關閉",
    discard: "捨棄",
    clear: "清除",
    cancel: "取消",
    edit: "編輯",
    copy: "複製",
    delete: "刪除",
    close: "關閉",
    done: "完成",
    ok: "確定",
    add: "新增",
    remove: "移除",
    update: "更新",
    back: "上一步",
    next: "下一步",
    retry: "重試",
    copy_id: "複製 ID",
    copied: "已複製",

    // Confirmation dialogs. `{{entityName}}` and `{{collectionName}}` are
    // i18next interpolations — dropping them prints the placeholder verbatim.
    delete_confirmation_title: "確定刪除？",
    delete_confirmation_body: "這將刪除這筆資料，確定嗎？",
    delete_multiple_confirmation_body: "這將刪除所選的資料，確定嗎？",
    delete_entity_confirm_title: "要刪除這筆{{entityName}}嗎？",
    unsaved_changes_title: "尚未儲存的變更",
    unsaved_changes_body: "你有尚未儲存的變更，要捨棄嗎？",
    discard_changes: "捨棄變更",
    keep_editing: "繼續編輯",
    are_you_sure_leave: "確定要離開這個頁面嗎？",
    unsaved_changes: "你在這個{{collectionName}}中有尚未儲存的變更。",
    unsaved_changes_in_entity: "這筆資料有尚未儲存的變更。",
    delete_selected: "刪除",
    cannot_delete_selected: "所選資料中至少有一筆無法刪除",

    // Collection table
    search: "搜尋",
    find_by_id: "以 ID 尋找",
    find_entity_by_id: "以 ID 尋找資料",
    filter: "篩選",
    filters: "篩選條件",
    clear_filter: "清除篩選",
    clear_filter_sort: "清除篩選／排序",
    clear_sort: "清除排序",
    no_items: "沒有資料",
    no_entries_found: "找不到資料",
    all_entries_loaded: "已載入全部 {{count}} 筆",
    create_your_first_entry: "建立第一筆資料",
    no_results_filter_sort: "目前的篩選／排序沒有結果",
    no_results: "目前的篩選／排序沒有結果",
    refresh_data: "重新載入",
    so_empty: "這裡還沒有資料…",
    multiple_entities: "多筆資料",
    loading: "載入中",
    column_cannot_be_edited: "這個欄位無法直接編輯",

    // Errors and validation
    required: "必填",
    this_form_has_errors: "這個表單有錯誤",
    error: "錯誤",
    error_saving_data: "儲存資料時發生錯誤",
    saving_data: "正在儲存資料",
    entities_saved: "筆資料已儲存",
    internal_error: "內部錯誤",
    this_entity_not_exist: "資料庫中沒有這筆資料",
    entity_not_found: "找不到資料",
    error_deleting: "刪除時發生錯誤",
    error_before_delete: "刪除前發生錯誤",
    this_form_has_been_modified: "這個表單已被修改",
    value_updated_elsewhere: "這個值已在別處被更新",

    // Data types, shown in table headers and property previews
    type: "類型",
    string: "文字",
    number: "數字",
    boolean: "是／否",
    date: "日期",
    map: "群組",
    array: "清單",

    // The tag reference picker on the news collection
    reference_not_set: "尚未選擇",
    reference_does_not_exist: "參照的資料不存在",
    value_is_not_reference: "這個值不是參照。",
    click_to_edit: "點擊編輯",
    data_is_not_array_of_references: "資料不是參照清單",
    collection_does_not_exist: "指定的集合不存在，請查看主控台",
    add_specific: "新增{{name}}",
    select_specific: "選擇{{name}}",
    select_from: "從{{name}}選擇",
    add_entry: "新增",
    add_on_top: "加到最上面",
    add_below: "加到下面",
    add_to_field: "新增至{{fieldName}}",

    // Navigation and chrome
    open_menu: "開啟選單",
    close_drawer: "收起側邊欄",
    navigation_drawer: "導覽側邊欄",
    home: "首頁",
    log_out: "登出",
    search_collections: "搜尋集合",
    page_not_found: "找不到頁面",
    page_not_found_body: "這個頁面不存在，或你沒有存取權限",
    back_to_home: "回到首頁",
    views_group: "檢視",
    no_collections_found: "找不到集合",

    // Theme
    dark_mode: "深色",
    light_mode: "淺色",
    system_mode: "系統",

    // Misc
    copy_url_to_clipboard: "複製網址",
    open_image_in_new_tab: "在新分頁開啟圖片",
    id: "ID",
    created_on: "建立於",
    email: "電子郵件",
    yes: "是",
    no: "否",
    all: "全部",
};
